export class ProductCapacityService {
  getRemainingCapacity(product, allProducts, bookingDate, items, startTime) {
    if (product.isPackage) {
      return this.getPackageRemainingCapacity(product, allProducts, bookingDate, items, startTime);
    }
    const bookingRestrictions = this.getBookingRestrictions(items, bookingDate, allProducts, startTime);
    if (product.sessions) {
      const session = product.sessions.find(s => s.startTime === startTime);
      if (!session) {
        return Infinity;
      }
      if (session.isRestricted || this.hasBookingRestrictions(bookingRestrictions, session.resources, product, allProducts, startTime)) {
        return 0;
      }
      const remainingResourceCapacity = this.getRemainingSessionResourceCapacity(session, items);
      const remainingSessionCapacity = this.getRemainingSessionCapacity(session, product, items);
      return Math.min(remainingResourceCapacity, remainingSessionCapacity);
    }
    if (this.hasBookingRestrictions(bookingRestrictions, product.resources, product, allProducts, startTime)) {
      return 0;
    }
    const pendingQuantity = items.filter(item => item.productId === product.productId).reduce((total, item) => total + this.getTicketQuantity(item), 0);
    if (typeof product.managedQuantity === 'number') {
      return product.managedQuantity - pendingQuantity;
    }
    return this.getRemainingCapacityForNonSessionProduct(product, items);
  }
  /**
   * Determine whether the resources for a game-based session have sufficient capacity to fit guests split between them
   * e.g. for 7 guests with a max capacity of 6 per resources, we need 2 resources with at least 4 capacity
   */
  isSessionAvailableForGuests(session, guests, capacity) {
    if (!session?.resources) {
      return false;
    }
    const resourcesRequired = Math.ceil(guests / capacity);
    const guestsPerResource = Math.ceil(guests / resourcesRequired);
    const availableResources = session.times?.[0].resources.filter(x => x.gameBasedCapacity && x.gameBasedCapacity >= guestsPerResource).length;
    return availableResources >= resourcesRequired;
  }
  getPackageRemainingCapacity(product, allProducts, bookingDate, items, startTime) {
    if (!product.packageItems?.length) {
      return Infinity;
    }
    return product.packageItems.reduce((remainingCapacity, packageItem) => {
      const packageItemProduct = allProducts[packageItem.productId];
      if (!packageItemProduct) {
        return remainingCapacity;
      }
      const productRemainingCapacity = this.getRemainingCapacity(packageItemProduct, allProducts, bookingDate, items, startTime);
      return Math.min(remainingCapacity, productRemainingCapacity);
    }, Infinity);
  }
  hasBookingRestrictions(bookingRestrictions, resources, product, products, startTime) {
    if (product.packageItems && product.packageItems.length) {
      return this.packageHasBookingRestrictions(bookingRestrictions, product, products, startTime);
    }
    if (bookingRestrictions.length > 0 && resources) {
      const resourceIds = resources.map(x => x.id);
      const restrictions = bookingRestrictions.filter(restriction => {
        return resourceIds.includes(restriction.resourceId) && restriction.productId !== product.productId;
      });
      if (restrictions.length === 0) {
        return false;
      }
      if (restrictions.some(r => r.bookingRestriction === 2 /* ECapacityBookingRestriction.singleTicketType */)) {
        return true;
      }
      const parentProductIds = restrictions.map(x => x.parentProductId);
      if (parentProductIds.some(id => id !== product.parentProductId)) {
        return true;
      }
    }
    return false;
  }
  packageHasBookingRestrictions(bookingRestrictions, product, products, startTime) {
    if (!product.packageItems) {
      return false;
    }
    for (const packageItem of product.packageItems) {
      const packageProduct = products[packageItem.productId];
      if (!packageProduct) {
        continue;
      }
      let resources = packageProduct.resources;
      let isSessionRestricted = false;
      if (typeof startTime === 'number') {
        const session = packageProduct.sessions && packageProduct.sessions.find(s => s.startTime === startTime);
        if (session?.resources) {
          resources = session.resources;
        }
        if (session?.isRestricted) {
          isSessionRestricted = true;
        }
      }
      const restrictions = isSessionRestricted || this.hasBookingRestrictions(bookingRestrictions, resources, packageProduct, products);
      if (restrictions) {
        return true;
      }
    }
    return false;
  }
  getRemainingSessionResourceCapacity(session, items) {
    if (typeof session.remainingResourceCapacity !== 'number') {
      return Infinity;
    }
    let remainingCapacity = session.remainingResourceCapacity;
    if (!session.times?.length) {
      return remainingCapacity;
    }
    session.times.forEach(sessionResourceTime => {
      const itemsWithSameResource = items.filter(item => {
        if (item.bookingDateIndex !== session.dateIndex) {
          return false;
        } else if (!item.sessionStart || !item.sessionEnd) {
          return item.product.resources?.some(resource => {
            return sessionResourceTime.resourceIds.includes(resource.id);
          });
        }
        const productSession = item.product.sessions?.find(s => s.startTime === item.sessionStart);
        if (!productSession?.times?.length) {
          return false;
        }
        let hasSharedResources = false;
        productSession.times.forEach(itemResourceTime => {
          const itemOverlapsWithSession = this.resourceTimesOverlap(session, sessionResourceTime, itemResourceTime);
          if (itemOverlapsWithSession && itemResourceTime.resourceIds.some(id => sessionResourceTime.resourceIds.includes(id))) {
            hasSharedResources = true;
          }
        });
        return hasSharedResources;
      });
      const quantity = itemsWithSameResource.reduce((total, item) => total + this.getTicketQuantity(item), 0);
      const remainingForTime = sessionResourceTime.remainingCapacity - quantity;
      if (remainingForTime < remainingCapacity) {
        remainingCapacity = remainingForTime;
      }
    });
    return remainingCapacity;
  }
  getRemainingCapacityForNonSessionProduct(product, items) {
    if (!product.availability) {
      return Infinity;
    }
    const totalRemainingConsideringResource = product.availability.remainingResourceCapacity;
    const totalRemaining = product.availability.capacityRemaining || Infinity;
    const hasResourceCapacity = typeof totalRemainingConsideringResource === 'number';
    if (!hasResourceCapacity || totalRemaining < totalRemainingConsideringResource) {
      const ticketQuantityForProduct = items.filter(item => item.productId === product.productId && item.bookingDateIndex === product.availability?.dateIndex).reduce((total, item) => total + this.getTicketQuantity(item), 0);
      return totalRemaining - ticketQuantityForProduct;
    } else if (!product.resources?.length) {
      return totalRemainingConsideringResource;
    }
    const itemsSharingResources = items.filter(item => {
      if (item.bookingDateIndex !== product.availability?.dateIndex) {
        return false;
      }
      if (item.product.resources?.length) {
        return item.product.resources.some(itemProductResource => {
          return product.resources?.some(productResource => productResource.id === itemProductResource.id);
        });
      }
      const session = item.product.sessions?.find(session => session.startTime === item.sessionStart);
      if (!item.sessionStart || !session?.resources?.length) {
        return false;
      }
      return session.resources.some(resource => product.resources?.some(productResource => productResource.id === resource.id));
    });
    return totalRemainingConsideringResource - itemsSharingResources.reduce((total, item) => total + this.getTicketQuantity(item), 0);
  }
  resourceTimesOverlap(session, sessionResourceTime, itemResourceTime) {
    return itemResourceTime.resourceStartTime >= sessionResourceTime.resourceStartTime && (itemResourceTime.resourceStartTime < sessionResourceTime.resourceEndTime || session.endTime < session.startTime) || itemResourceTime.resourceStartTime <= sessionResourceTime.resourceStartTime && itemResourceTime.resourceEndTime > sessionResourceTime.resourceStartTime;
  }
  getRemainingSessionCapacity(session, product, items) {
    if (typeof session.remainingSessionCapacity !== 'number') {
      return Infinity;
    }
    const ticketQuantityForSession = items.filter(item => this.isSameProductSession(item, session, product)).reduce((total, item) => total + this.getTicketQuantity(item), 0);
    return session.remainingSessionCapacity - ticketQuantityForSession;
  }
  isSameProductSession(item, session, product) {
    return item.sessionStart === session.startTime && item.parentProductId === product.parentProductId && item.bookingDateIndex === session.dateIndex;
  }
  getTicketQuantity(item) {
    return item.quantity * (item.product.requiredGroupSize || 1);
  }
  getBookingRestrictions(pendingOrders, bookingDate, products, startTime) {
    if (pendingOrders.length === 0) {
      return [];
    }
    const bookingRestrictions = [];
    const dateIndex = this.toDateIndex(bookingDate);
    const pendingOrdersForDate = pendingOrders.filter(x => x.bookingDateIndex === dateIndex);
    pendingOrdersForDate.forEach(pendingOrder => {
      const product = products[pendingOrder.productId];
      if (!product || typeof startTime === 'number' && typeof pendingOrder.sessionStart === 'number' && pendingOrder.sessionStart !== startTime) {
        return;
      }
      let resources;
      if (product.sessions?.length) {
        const session = product.sessions.find(s => s.startTime === startTime);
        resources = session ? session.resources : null;
      } else {
        resources = product.resources;
      }
      resources?.forEach(resource => {
        if (resource.allowMultipleBookings && resource.bookingRestriction > 0 /* ECapacityBookingRestriction.multipleProducts */) {
          bookingRestrictions.push({
            productId: pendingOrder.productId,
            parentProductId: pendingOrder.parentProductId,
            resourceId: resource.id,
            bookingRestriction: resource.bookingRestriction
          });
        }
      });
    });
    return bookingRestrictions;
  }
  toDateIndex(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const result = `${year}${month}${day}`;
    return parseInt(result, 10);
  }
}